import { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config/apiConfig';

export const DEFAULT_SHIPPING_SETTINGS = {
  fixedCharge: 18,
  freeShippingThreshold: 260,
  shippingLabel: 'Shirts',
  currencySymbol: '$',
  freeShippingMessage: 'Free shipping & returns on orders over $260.00'
};

const toNumber = (value, fallback) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue >= 0 ? numberValue : fallback;
};

const toText = (value, fallback) => {
  const text = String(value ?? '').trim();
  return text || fallback;
};

export const normalizeShippingSettings = (raw = {}) => {
  const fixedCharge = toNumber(
    raw.fixedCharge ?? raw.FixedCharge,
    DEFAULT_SHIPPING_SETTINGS.fixedCharge
  );
  const freeShippingThreshold = toNumber(
    raw.freeShippingThreshold ?? raw.FreeShippingThreshold,
    DEFAULT_SHIPPING_SETTINGS.freeShippingThreshold
  );
  const currencySymbol = toText(
    raw.currencySymbol ?? raw.CurrencySymbol,
    DEFAULT_SHIPPING_SETTINGS.currencySymbol
  );
  const freeShippingMessage = toText(
    raw.freeShippingMessage ?? raw.FreeShippingMessage,
    `Free shipping & returns on orders over ${currencySymbol}${freeShippingThreshold.toFixed(2)}`
  );

  return {
    fixedCharge,
    freeShippingThreshold,
    shippingLabel: toText(raw.shippingLabel ?? raw.ShippingLabel, DEFAULT_SHIPPING_SETTINGS.shippingLabel),
    currencySymbol,
    freeShippingMessage
  };
};

export const calculateShippingAmount = (subtotal, settings = DEFAULT_SHIPPING_SETTINGS) => {
  const normalized = normalizeShippingSettings(settings);
  const subtotalAmount = toNumber(subtotal, 0);

  return normalized.freeShippingThreshold > 0 && subtotalAmount > normalized.freeShippingThreshold
    ? 0
    : normalized.fixedCharge;
};

export const formatShippingCurrency = (value, settings = DEFAULT_SHIPPING_SETTINGS) => {
  const normalized = normalizeShippingSettings(settings);
  return `${normalized.currencySymbol}${toNumber(value, 0).toFixed(2)}`;
};

export const useShippingSettings = () => {
  const [shippingSettings, setShippingSettings] = useState(DEFAULT_SHIPPING_SETTINGS);

  useEffect(() => {
    let isMounted = true;

    fetch(`${API_BASE_URL}/api/shipping-settings`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (isMounted && data) {
          setShippingSettings(normalizeShippingSettings(data));
        }
      })
      .catch((err) => {
        console.error('Failed to fetch shipping settings:', err);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return shippingSettings;
};
